// src/app/[lang]/shop/carpets/ProductDisplay.jsx

'use client'

import { useEffect, useCallback, useState, useOptimistic, useTransition, use } from 'react';
import { useInView } from 'react-intersection-observer';
import { useSearchParams } from 'next/navigation';
import ProductCard from '@/components/Product/ProductCard/ProductCard';
import { fetchMoreProducts } from '@/actions/products.actions';
import styles from '@/app/[lang]/shop/shop-category.module.scss';


export default function ProductDisplay({ initialDataPromise, lang, carpetsRouteDict, PaginationComponent }) {

  const initialData = use(initialDataPromise);
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();

  const [showPagination, setShowPagination] = useState(true);
  const [hasMore, setHasMore] = useState(initialData.currentPage < initialData.totalPages);
  const [currentPage, setCurrentPage] = useState(initialData.currentPage);
  const [renderedCards, setRenderedCards] = useState(initialData.preRenderedCards || []);
  const [totalPages, setTotalPages] = useState(initialData.totalPages);
  
  // Use optimistic UI for smoother loading experience
  const [optimisticCards, addOptimisticCards] = useOptimistic(
    renderedCards,
    (state, newCards) => [...state, ...newCards]
  );

  const { ref, inView } = useInView({
    threshold: 0.1,
    rootMargin: '200px',
  });

  useEffect(() => {
    setShowPagination(searchParams.size === 0);
  }, [searchParams]);

  // Reset state when search params change
  useEffect(() => {
    setRenderedCards(initialData.preRenderedCards || []);
    setCurrentPage(initialData.currentPage);
    setTotalPages(initialData.totalPages);
    setHasMore(initialData.currentPage < initialData.totalPages);
  }, [searchParams, initialData]);

  const loadMoreContent = useCallback(async () => {
    if (isPending || !hasMore || currentPage >= totalPages) return;

    const nextPage = currentPage + 1;
    const searchParamsObject = Object.fromEntries(searchParams);

    // Using server action with transitions for better UX
    startTransition(async () => {
      try {
        const newData = await fetchMoreProducts(lang, searchParamsObject, nextPage);
        
        if (!newData.products || newData.products.length === 0) {
          setHasMore(false);
          return;
        }
        
        const newCards = newData.products.map((product) => ({
          productId: product.productID,
          card: <ProductCard
            key={product.productID}
            product={product}
            lang={lang}
            priority={false}
          />
        }));

        setRenderedCards(prev => [...prev, ...newCards]);
        setCurrentPage(nextPage);
        setTotalPages(newData.totalPages);
        setHasMore(nextPage < newData.totalPages);
      } catch (error) {
        console.error('Error loading more content:', error);
        setHasMore(false);
      }
    });
  }, [currentPage, hasMore, isPending, lang, searchParams, totalPages]);

  useEffect(() => {
    if (inView && !isPending && hasMore) {
      loadMoreContent().then();
    }
  }, [inView, isPending, hasMore, loadMoreContent]);

  if (renderedCards.length === 0) {
    return <p>{carpetsRouteDict.noResults}</p>;
  }

  return (
    <>
      <div className={styles.productsContainer}>
        {optimisticCards.map(({ card }) => card)}
        {hasMore && (
          <div ref={ref} style={{ height: '1px' }} />
        )}
      </div>
      {isPending && <p>{carpetsRouteDict.loadingMore}</p>}
      {showPagination && PaginationComponent}
    </>
  );
}
